import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useGetProvidersInfiniteList } from 'services/providers';
import {
  DDEX_STANDARD,
  DISTRIBUTION_FORM,
  ISaleChannelForm,
} from 'services/salesChannel/types';
import { ACTIVE_STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  booleanSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import * as Yup from 'yup';
import { requiredEnumSelect } from '../../../../utils/global/validation.utils';
import { IFormikProps, IFormProps } from '../types';
import Providers from './tabs/Providers';

export const saleChannelInitialValue: ISaleChannelForm = {
  name: '',
  distributionFormId: DISTRIBUTION_FORM.unknown,
  isActive: ACTIVE_STATUS.Inactive,
  messageRecipientId: '',
  messageRecipientName: '',
  ddexStandard: DDEX_STANDARD.unknown,
  file: null,
  logoImageUrl: null,
  defaultProviderId: '',
  defaultProviderName: '',
  isPredefined: false,
  canBeSent: false,
  isGlobalSendAllowed: false,
  requireVideoRectangleCover: false,
  acceptVideo: false,
  acceptVideoWithTrack: false,
};

const saleChannelValidationSchema = Yup.object().shape({
  name: Yup.string().required(() => i18next.t('formik-error.required')),
  distributionFormId: requiredEnumSelect(DISTRIBUTION_FORM),
  messageRecipientName: Yup.lazy((_, options) =>
    Yup.string().when('messageRecipientId', {
      is: (id: string | undefined) => id?.length,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.nullable(),
    })
  ),
  messageRecipientId: Yup.lazy((_, options) =>
    Yup.string().when('messageRecipientName', {
      is: (name: string | undefined) => name?.length,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.nullable(),
    })
  ),
});

export enum SaleChannelTabs {
  providers,
}

export interface SaleChannelFormProps
  extends FormikConfig<ISaleChannelForm>,
    IFormProps,
    II18nextPrefix {}

const SaleChannelForm: React.FC<SaleChannelFormProps> & {
  getRouterTabs: (disabledChildren?: string[]) => any[];
} = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = saleChannelInitialValue,
  backPath,
  customBackItem,
  disableTabs,
  onSubmit,
  ...props
}) => {
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();

  const providers = useGetProvidersInfiniteList({ itemCount: 999 });

  return (
    <Formik
      {...props}
      initialStatus={status}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={saleChannelValidationSchema}
      onSubmit={onSubmit}>
      {({
        values,
        status,
        errors,
        setFieldValue,
      }: IFormikProps<ISaleChannelForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={status?.displayMode ? values.name : undefined}
              customBackItem={customBackItem}
              path={backPath}
              topbarContent={
                <div className='w-full flex justify-between items-center'>
                  <div className='w-48'>
                    {!status?.displayMode && (
                      <FormInput.Text
                        name='name'
                        label={t('input.label.name')}
                        autoFocus
                        disabled={!status?.displayMode && values.isPredefined}
                      />
                    )}
                  </div>

                  <div className='flex items-center gap-2'>
                    {status?.displayMode ? (
                      <LabeledStatus.Active status={initialValues.isActive} />
                    ) : (
                      <div className='w-44'>
                        <FormInput.Select
                          name='isActive'
                          label={t('input.label.active')}
                          options={getSelectOptions(
                            ACTIVE_STATUS,
                            'enum.ACTIVE_STATUS'
                          )}
                          isDisabled={
                            !status?.displayMode && values.isPredefined
                          }
                        />
                      </div>
                    )}
                    {topbarContent}
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Image
                  //  size={true}
                  fullResolutionUrl={values.logoImageUrl ?? undefined}
                  previewUrl={values.logoImageUrl ?? undefined}
                  isError={errors.file !== undefined}
                  editable={!status?.displayMode}
                  onChange={file => setFieldValue('file', file)}
                  onCancel={action => {
                    setFieldValue('logoImageUrl', '');
                    setFieldValue('file', null);
                    action.close();
                  }}
                  onDelete={
                    initialValues.logoImageUrl
                      ? action => {
                          setFieldValue('logoImageUrl', '');
                          setFieldValue('file', null);
                          action.close();
                          action.close();
                        }
                      : undefined
                  }
                  accept={''}
                />
                <InfoBox.Items columns={3}>
                  <FormInput.Select
                    name='distributionFormId'
                    label={t('input.label.distribution')}
                    options={getSelectOptions(
                      DISTRIBUTION_FORM,
                      'enum.DISTRIBUTION'
                    )}
                    isDisabled={!status?.displayMode && values.isPredefined}
                  />
                  <FormInput.Select
                    name='defaultProviderId'
                    label={t('input.label.provider')}
                    isDisabled={
                      initialValues.name === '' ||
                      (status?.displayMode && values.isPredefined)
                    }
                    options={providers.data?.pages.flat().map(provider => ({
                      value: provider.id,
                      label: provider.name,
                    }))}
                  />
                  <FormInput.Text
                    name='messageRecipientId'
                    label={t('input.label.messageRecipientId')}
                    disabled={!status?.displayMode && values.isPredefined}
                  />

                  <FormInput.Text
                    name='messageRecipientName'
                    label={t('input.label.messageRecipientName')}
                    disabled={!status?.displayMode && values.isPredefined}
                  />
                  <FormInput.Select
                    label={t('input.label.canBeSent')}
                    name='canBeSent'
                    options={booleanSelectOptions}
                    isDisabled={!status?.displayMode && values.isPredefined}
                  />
                  <FormInput.Select
                    label={t('input.label.isGlobalSendAllowed')}
                    name='isGlobalSendAllowed'
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    label={t('input.label.acceptVideo')}
                    name='acceptVideo'
                    options={booleanSelectOptions}
                    isDisabled={!status?.displayMode && values.isPredefined}
                  />
                  <FormInput.Select
                    label={t('input.label.acceptVideoWithTrack')}
                    name='acceptVideoWithTrack'
                    options={booleanSelectOptions}
                    isDisabled={!status?.displayMode && values.isPredefined}
                  />
                  <FormInput.Select
                    label={t('input.label.requireVideoRectangleCover')}
                    name='requireVideoRectangleCover'
                    options={booleanSelectOptions}
                    isDisabled={!status?.displayMode && values.isPredefined}
                  />
                </InfoBox.Items>
              </InfoBox>

              <TabsNavigation
                tabs={[
                  {
                    value: SaleChannelTabs.providers,
                    name: 'providers',
                    permissionName: 'ProvidersPermissionsLIST**',
                  },
                ]}
                i18nextPrefix={'sales-channel'}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

SaleChannelForm.getRouterTabs = (disabledChildren?: string[]) =>
  [{ path: 'providers', element: <Providers /> }].filter(
    children => !disabledChildren?.includes(children.path)
  );

export default SaleChannelForm;
